import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/design-system/atoms/Button';
import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { RichTextEditor } from '@/design-system/atoms/RichTextEditor';
import { Tabs } from '@/design-system/atoms/Tabs';
import { Title } from '@/design-system/atoms/Title';
import { useLinksource } from '@/hooks/useLinksource/useLinksource';
import { useOnlineSchedulingTabs } from '@/hooks/useOnlineSchedulingTabs';
import { useScrollToElementInHash } from '@/hooks/useScrollToElementInHash';
import { tokenReplace } from '@/utils/tokenReplace';

import styles from './OnlineScheduling.module.scss';

const OnlineScheduling = ({
    title,
    subTitle,
    body,
    cta,
    simpleWidgetAPIKey,
    scriptSource,
    embeddedWidgetAPIKey,
    departmentId,
    visitTypeId,
    providerId,
    onlineBookingAvailable,
    acceptingNewPatients,
    url,
    id,
    htmlId,
    iframeTitle = 'View available dates and times for appointments.',
    myChartExistingPatientUrl,
    newPatientSubtitle,
    newPatientBody,
    availableSoonOnline = false,
    availableOnline = false,
    newPatientNoAvailabilityText,
    phoneNumber,
    ...props
}) => {
    const DEFAULT_FALLBACK_PHONE = '800-247-9580';
    const scriptId = 'scheduling-widget-script';
    const containerId = id;
    const elRef = useRef(null);
    const schedulingRef = useRef(null);
    const scriptRef = useRef(null);
    const isWidgetRendered = useRef(false);
    const [iframehasLoaded, setIframehasLoaded] = useState(false);
    const [intervalId, setIntervalId] = useState(null);
    const [iframe, setIFrame] = useState(null);
    const { linksource, isLinksourceReady } = useLinksource({
        url,
        willUpdate: false,
        withLinksource: false,
    });

    const hasOnlineAvailability =
        onlineBookingAvailable && (Boolean(availableSoonOnline) || Boolean(availableOnline));
    const effectivePhoneNumber =
        typeof phoneNumber === 'string' && phoneNumber.trim()
            ? phoneNumber.trim()
            : DEFAULT_FALLBACK_PHONE;

    const processedNoAvailabilityText = newPatientNoAvailabilityText
        ? tokenReplace(newPatientNoAvailabilityText, {
              phone: `<a href="tel:${effectivePhoneNumber}">${effectivePhoneNumber}</a>`,
          })
        : newPatientNoAvailabilityText;

    const effectiveCta =
        myChartExistingPatientUrl && cta ? { ...cta, href: myChartExistingPatientUrl } : cta;

    const { tabs } = useOnlineSchedulingTabs({
        acceptingNewPatients,
        body,
        containerId,
        cta: effectiveCta,
        newPatientBody,
        newPatientSubtitle,
        subTitle,
        title,
        iframe,
        available: hasOnlineAvailability,
        newPatientNoAvailabilityText: processedNoAvailabilityText,
    });

    useScrollToElementInHash(schedulingRef, iframehasLoaded);

    useEffect(() => {
        if (!iframe) return undefined;
        const noop = function noopScrollIntoView() {};
        iframe.scrollIntoView = noop;
        return () => {
            if (iframe.scrollIntoView === noop) {
                delete iframe.scrollIntoView;
            }
        };
    }, [iframe]);

    const createWidget = useCallback(() => {
        if (
            !acceptingNewPatients ||
            !hasOnlineAvailability ||
            !window?.mychartWidget ||
            isWidgetRendered.current
        ) {
            return;
        }

        window.mychartWidget.renderWidget({
            url,
            apiKey: embeddedWidgetAPIKey,
            widgetType: 'openscheduling',
            containerId,
            additionalParams: {
                dept: '',
                vt: visitTypeId ?? '',
                id: providerId ?? '',
                linksource: linksource ?? '',
            },
        });

        isWidgetRendered.current = true;
    }, [
        acceptingNewPatients,
        hasOnlineAvailability,
        url,
        embeddedWidgetAPIKey,
        containerId,
        visitTypeId,
        providerId,
        linksource,
    ]);

    // On mount fetch the script and create the widget
    // If the script is already loaded, create the widget
    // If the script is not loaded, load the script and create the widget
    useEffect(() => {
        if (
            scriptRef.current ||
            !onlineBookingAvailable ||
            !hasOnlineAvailability ||
            !isLinksourceReady
        ) {
            return;
        } else if (document.getElementById(scriptId) && !isWidgetRendered.current) {
            createWidget();
            return;
        }

        const script = document.createElement('script');
        script.src = scriptSource;
        script.async = true;
        script.id = scriptId;
        script.onload = createWidget;
        scriptRef.current = script;

        document.body.appendChild(script);
    }, [
        scriptRef,
        elRef,
        scriptSource,
        createWidget,
        onlineBookingAvailable,
        hasOnlineAvailability,
        isLinksourceReady,
    ]);

    // The widget does not have an option to include a title to the iframe
    // Adding the iframe title by DOM manipulation when the iframe has been rendered
    useEffect(() => {
        if (!intervalId && elRef?.current && !iframehasLoaded && hasOnlineAvailability) {
            setIntervalId(
                setInterval(() => {
                    const frame = elRef.current?.querySelector('iframe') || null;
                    if (frame) {
                        setIframehasLoaded(true);
                        setIFrame(frame);
                        frame.title = iframeTitle;
                    }
                }, 100),
            );
        }
    }, [iframehasLoaded, intervalId, iframeTitle, hasOnlineAvailability]);

    useEffect(() => {
        if (iframehasLoaded) {
            clearInterval(intervalId);
        }
    }, [iframehasLoaded, intervalId]);

    if (!onlineBookingAvailable) {
        return null;
    }

    return (
        <ContentContainer
            ref={schedulingRef}
            id={htmlId}
            className={styles[`online-scheduling`]}
            {...props}
        >
            {title && (
                <Title
                    className={styles[`online-scheduling__title`]}
                    content={title}
                    variant={Title.VARIANT.H3}
                    tag={Title.TAG.H2}
                />
            )}
            <div ref={elRef}>
                {tabs?.length && <Tabs className={styles[`online-scheduling__tabs`]} tabs={tabs} />}
            </div>
        </ContentContainer>
    );
};

OnlineScheduling.propTypes = {
    /**
     * The title of the section.
     */
    title: PropTypes.string,
    /**
     * The subTitle of the section.
     */
    subTitle: PropTypes.string,
    /**
     * The body of the section.
     */
    body: RichTextEditor.propTypes.content,
    /**
     * The call to actions of the section.
     */
    cta: Button.propTypes,
    /**
     * The simple widget API key.
     */
    simpleWidgetAPIKey: PropTypes.string,
    /**
     * The script source.
     */
    scriptSource: PropTypes.string,
    /**
     * The embedded widget API key.
     */
    embeddedWidgetAPIKey: PropTypes.string,
    /**
     * The department ID.
     */
    departmentId: PropTypes.string,
    /**
     * The visit type ID.
     */
    visitTypeId: PropTypes.string,
    /**
     * The provider ID.
     */
    providerId: PropTypes.string,
    /**
     * If online booking is available.
     */
    onlineBookingAvailable: PropTypes.bool,
    /**
     * If accepting new patients.
     */
    acceptingNewPatients: PropTypes.bool,
    /**
     * Iframe title
     */
    iframeTitle: PropTypes.string,
    /**
     * FAD existing patient URL
     */
    myChartExistingPatientUrl: PropTypes.string,
    /**
     * Whether the provider has online appointments within 180 days
     */
    availableSoonOnline: PropTypes.bool,
    /**
     * Whether the provider has online appointments within 545 days
     */
    availableOnline: PropTypes.bool,
    /**
     * Text to display when no availability for new patients
     */
    newPatientNoAvailabilityText: PropTypes.string,
    /**
     * Practice phone number for {phone} token replacement
     */
    phoneNumber: PropTypes.string,
};

export default OnlineScheduling;
